import React, { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { getAPIBaseURL } from '../../services/helpers'
import { getData, patchData } from '../../services/request'
import AdminEmploymeUserSection from '../components/AdminEmploymeUserSection'

const InfoRow = ({ label, value }) => (
  <div className="grid grid-cols-3 gap-4 border-b border-gray-100 py-3 text-sm">
    <div className="col-span-1 font-medium text-gray-600 whitespace-nowrap">{label}</div>
    <div className="col-span-2 text-gray-900">{value || '—'}</div>
  </div>
)

const AdminEmployerDetails = () => {
  const { userId } = useParams()
  const [company, setCompany] = useState(null)
  const [error, setError] = useState('')
  const [actioning, setActioning] = useState(false)

  const fetchCompany = async () => {
    const url = `${getAPIBaseURL()}/employme/admin/companies/by-owner/${userId}`
    const res = await getData(url, {}, {})
    if (res.error === '' && res.result?.status === 200) {
      setCompany(res.result.data?.data || res.result.data)
    } else {
      setCompany(null)
    }
  }

  useEffect(() => {
    fetchCompany()
  }, [userId])

  const formatDate = (iso) => {
    try {
      return new Date(iso).toLocaleString()
    } catch {
      return iso || '—'
    }
  }

  const verifyDoc = async ({ companyId, docId, status }) => {
    setActioning(true)
    setError('')
    const url = `${getAPIBaseURL()}/employme/admin/companies/${companyId}/verification-docs/${docId}`
    const headers = { 'Content-Type': 'application/json' }
    let payload = { status }
    if (status === 'REJECTED') {
      const reason = window.prompt('Rejection reason (optional):') || ''
      payload = { status, rejectionReason: reason }
    }
    const res = await patchData(url, headers, payload)
    setActioning(false)
    if (res.error === '' && res.result?.status === 200) {
      setCompany(res.result.data?.data || res.result.data)
    } else {
      setError(res.result?.data?.message || res.result?.data?.data || 'Failed to update document status')
    }
  }

  return (
    <section className="mx-auto max-w-4xl">
      <AdminEmploymeUserSection
        userId={userId}
        expectedRole="EMPLOYER"
        backPath="/admin/app/employme/employers"
        backLabel="Back to Employers"
        roleLabel="Employer"
      />

      {error && (
        <div className="mb-4 mt-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>
      )}

      <div className="mt-6 rounded-lg border border-gray-200 bg-white shadow-sm">
        <div className="border-b border-gray-200 px-6 py-4">
          <h2 className="text-lg font-bold text-gray-900">Company</h2>
          <p className="mt-1 text-xs text-gray-500">
            Verification documents submitted by this employer.
          </p>
        </div>
        {!company ? (
          <div className="px-6 py-4 text-sm text-gray-500">No company record found.</div>
        ) : (
          <div className="px-6 py-4">
            <div className="mb-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
              <InfoRow label="Company name" value={company.name} />
              <InfoRow label="Status" value={company.status} />
              <InfoRow label="Industry" value={company.industry} />
              <InfoRow label="Size" value={company.size} />
              <InfoRow label="Website" value={company.website} />
              <InfoRow label="LinkedIn" value={company.linkedInUrl} />
              <InfoRow label="Registration No." value={company.registrationNumber} />
              <InfoRow label="Tax ID" value={company.taxId} />
            </div>

            <div className="rounded-lg border border-gray-200">
              <div className="border-b border-gray-200 bg-gray-50 px-4 py-3 text-sm font-semibold text-gray-900">
                Verification documents ({(company.verificationDocuments || []).length})
              </div>
              {(company.verificationDocuments || []).length === 0 ? (
                <div className="px-4 py-4 text-sm text-gray-500">No documents uploaded yet.</div>
              ) : (
                <ul className="divide-y divide-gray-200">
                  {(company.verificationDocuments || [])
                    .slice()
                    .reverse()
                    .map((d) => (
                      <li key={d._id || d.url} className="px-4 py-4">
                        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                          <div className="min-w-0">
                            <p className="text-sm font-semibold text-gray-900 truncate">{d.label || 'Document'}</p>
                            <p className="mt-1 text-xs text-gray-600">
                              Status: <span className="font-semibold">{d.status || 'PENDING'}</span>
                              {d.rejectionReason ? ` • Reason: ${d.rejectionReason}` : ''}
                            </p>
                            <p className="mt-1 text-xs text-gray-400">
                              Uploaded: {d.uploadedAt ? formatDate(d.uploadedAt) : '—'}
                            </p>
                          </div>
                          <div className="flex flex-wrap gap-2">
                            <a
                              href={d.url}
                              target="_blank"
                              rel="noreferrer"
                              className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-xs font-medium text-gray-700 hover:bg-gray-50 whitespace-nowrap"
                            >
                              View
                            </a>
                            <button
                              type="button"
                              disabled={actioning}
                              onClick={() => verifyDoc({ companyId: company._id, docId: d._id, status: 'VERIFIED' })}
                              className="rounded-lg border border-green-600 px-3 py-2 text-xs font-medium text-green-700 hover:bg-green-50 disabled:opacity-50 whitespace-nowrap"
                            >
                              Verify
                            </button>
                            <button
                              type="button"
                              disabled={actioning}
                              onClick={() => verifyDoc({ companyId: company._id, docId: d._id, status: 'REJECTED' })}
                              className="rounded-lg border border-red-600 px-3 py-2 text-xs font-medium text-red-700 hover:bg-red-50 disabled:opacity-50 whitespace-nowrap"
                            >
                              Reject
                            </button>
                          </div>
                        </div>
                      </li>
                    ))}
                </ul>
              )}
            </div>
          </div>
        )}
      </div>
    </section>
  )
}

export default AdminEmployerDetails
